import { createMuiTheme } from "@material-ui/core";
import {
  color,
  componentMetrics as m,
  mq,
  paletteTokens,
  spacingUnit,
  typographyTokens,
} from "../theme/tokens";

/**
 * Legacy MUI **v4** theme. Kept as `.js` on purpose — it is a v4 artefact and
 * is deleted wholesale at PM5 T6, so it is not worth converting to TypeScript.
 *
 * Since T0 every brand value comes from `src/theme/tokens.ts`, which also feeds
 * the v5+ theme (`src/theme/themeV5.ts`). Only the *source* of the values moved;
 * the theme this module exports is unchanged, value for value.
 * Do not hard-code a colour, spacing unit or font here — put it in tokens.ts.
 */
const muiTheme = createMuiTheme({
  palette: paletteTokens,
  spacing: spacingUnit,
  typography: typographyTokens,
  // v4-only crash workaround (#872) — do NOT port to themeV5. v4 ButtonBase's
  // focus-ripple effect calls `rippleRef.current.pulsate()` with no null check,
  // and under React 18 it can run before TouchRipple has mounted: "Cannot read
  // properties of null (reading 'pulsate')" → AppErrorFallback. Unfixed through
  // 4.12.4. Switch/Checkbox/Radio reach it via IconButton. Cost: v4 buttons lose
  // the keyboard-focus pulse; v9 buttons keep theirs. Dies with this file at T6.
  props: {
    MuiButton: { disableFocusRipple: true },
    MuiIconButton: { disableFocusRipple: true },
    MuiFab: { disableFocusRipple: true },
    MuiTab: { disableFocusRipple: true },
  },
  overrides: {
    MuiButton: {
      root: {
        border: `${m.button.borderWidth} solid ${color.ffGreen}`,
        borderRadius: m.button.borderRadius,
        margin: m.button.margin,
        [mq.sm]: {},
        [mq.md]: {},
        "&:hover": {
          backgroundColor: color.ffGreenHover,
          border: `${m.button.borderWidth} solid ${color.ffGreenHover}`,
          boxShadow: "none",
          transition: m.button.transition,
          "@media (hover: none)": {
            backgroundColor: color.ffGreenHover,
          },
        },
      },
      containedSecondary: {
        backgroundColor: color.buttonSecondary,
        color: color.white,
        border: "none",
        boxShadow: "none",
        "&:hover": {
          backgroundColor: color.buttonSecondaryHover,
          border: "none",
          boxShadow: "none",
          transition: m.button.transition,
        },
      },
      text: {
        padding: m.button.textPadding,
        lineHeight: m.button.lineHeight,
      },
      outlined: {
        lineHeight: m.button.lineHeight,
        padding: m.button.outlinedPadding,
        backgroundColor: "transparent",
        border: `${m.button.borderWidth} solid ${color.textPrimary}`,
        "&:hover": {
          backgroundColor: color.blackWash,
          border: `${m.button.borderWidth} solid ${color.textPrimary}`,
          color: color.textPrimary,
          boxShadow: "none",
          transition: m.button.transition,
        },
      },
      sizeSmall: {
        padding: m.button.smallPadding,
      },
      sizeLarge: {
        padding: m.button.largePadding,
        fontSize: m.button.largeFontSize,
        margin: m.button.largeMargin,
        [mq.sm]: {
          fontSize: m.button.largeFontSizeUp,
          lineHeight: m.button.largeLineHeightSm,
        },
        [mq.md]: {
          fontSize: m.button.largeFontSizeUp,
        },
      },
    },
    MuiDialog: {
      root: {
        // zIndex: "9999999 !important"
      },
    },
    MuiDivider: {
      root: {
        height: m.divider.height,
        backgroundColor: color.ffNavyLight,
      },
    },
    MuiAppBar: {
      root: {
        backgroundColor: color.ffNavy,
      },
    },
    MuiChip: {
      root: {
        backgroundColor: color.darkSurface,
        color: color.darkSurfaceText,
      },
    },
    MuiMenu: {
      paper: {
        backgroundColor: color.ffNavy,
        color: color.white,
      },
    },
    // NOTE: malformed since it was written — `padding` is a class-key slot, so
    // its value must be a style object, not the string "0". JSS ignores it, so
    // this block is a no-op. Preserved verbatim to keep the v4 theme unchanged;
    // deliberately NOT carried over to themeV5.ts. See the T0 mapping notes.
    MuiList: {
      padding: "0",
    },
    MuiListItem: {
      button: {
        "&:hover": {
          backgroundColor: color.darkSurface,
        },
      },
    },
    MuiTableCell: {
      root: {
        fontWeight: m.tableCell.fontWeight,
        padding: m.tableCell.padding,
      },
    },
    MuiFormControl: {
      root: {
        "& label": {
          color: color.ffNavy,
          fontWeight: m.formControlLabel.fontWeight,
          fontSize: m.formControlLabel.fontSize,
          width: m.formControlLabel.width,
        },
      },
    },
    MuiFormHelperText: {
      root: {
        color: color.ffGreen,
        "&:focus": {
          outline: "none",
        },
        "&:hover": {
          outline: "none",
        },
      },
    },
    MuiStepper: {
      root: {
        padding: m.stepper.padding,
        [mq.sm]: {
          padding: m.stepper.paddingUp,
        },
        [mq.md]: {
          padding: m.stepper.paddingUp,
        },
      },
    },
    MuiStepIcon: {
      root: {
        color: color.stepIconIdle,
        fontSize: m.stepIcon.fontSize,
        [mq.sm]: {
          fontSize: m.stepIcon.fontSize,
        },
        [mq.md]: {
          fontSize: m.stepIcon.fontSize,
        },
      },
      active: {
        color: `${color.ffGreen} !important`,
      },
      text: {
        fill: color.white,
      },
    },
    MuiTooltip: {
      popper: {
        zIndex: m.tooltip.popperZIndex,
      },
    },
    MuiStepLabel: {
      label: {
        color: color.ffNavy,
        fontSize: m.stepLabel.fontSize,
        lineHeight: m.stepLabel.lineHeight,
        [mq.sm]: {
          fontSize: m.stepLabel.fontSizeUp,
          lineHeight: m.stepLabel.lineHeight,
        },
        [mq.md]: {
          fontSize: m.stepLabel.fontSizeUp,
          lineHeight: m.stepLabel.lineHeight,
        },
      },
    },
    MuiFormLabel: {
      root: {
        lineHeight: m.formLabel.lineHeight,
        color: color.ffNavy,
        fontWeight: m.formLabel.fontWeight,
        fontSize: m.formLabel.fontSize,
      },
    },
    MuiInput: {
      underline: {
        borderBottom: "none",
        "&:before": {
          borderBottom: "none",
          content: "",
          "&:hover": {
            borderBottom: "none",
            outline: "none",
          },
        },
        "&:after": {
          borderBottom: "none",
          "&:hover": {
            borderBottom: "none",
            outline: "none",
          },
        },
        "&&&&:hover:before": {
          borderBottom: "none",
          outline: "none",
        },
      },
    },
    MuiSelect: {
      root: {
        marginTop: m.select.marginTop,
      },
    },
    MuiInputBase: {
      input: {
        marginTop: m.input.marginTop,
        fontSize: m.input.fontSize,
        lineHeight: m.input.lineHeight,
        // background:"#eee",
        padding: m.input.padding,
        // borderBottom: "none",
        border: `${m.input.borderWidth} solid ${color.inputBorder}`,
        borderRadius: m.input.borderRadius,
        "&:hover": {
          // borderBottom: "none"
        },
        "&:focus": {
          // borderBottom: "none"
          border: `${m.input.borderWidth} solid ${color.ffNavy}`,
        },
      },
    },
    MuiOutlinedInput: {
      input: {
        padding: m.outlinedInput.padding,
        fontSize: m.outlinedInput.fontSize,
      },
      root: {
        "&.Mui-error": {
          borderColor: `${color.errorMain} !important`,
        },
      },
    },
    MuiAlert: {
      message: {
        padding: m.alert.messagePadding,
      },
    },

    // MuiStepIcon: {
    //   root: {

    //   },
    //   active: {
    //     color: "red"
    //   }
    // }
  },
});

export default muiTheme;
